import { useState } from 'react';
import config from '../config';
import { subscribeToFloodAlerts } from '../utils/api';

function FloodAlertFormInner({ className }) {
  const [email, setEmail] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [alertType, setAlertType] = useState('email');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Call the real API endpoint
      await subscribeToFloodAlerts({
        email,
        zipCode,
        alertType
      });

      setSubmitted(true);
      // Reset form after successful submission
      setEmail('');
      setZipCode('');
    } catch (err) {
      setError('Failed to subscribe. Please try again later.');
      console.error('Error subscribing to flood alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-100 p-6 ${className}`}>
      <h2 className="text-2xl font-bold text-[#004C8C] mb-2">Flood Alert Notifications</h2>
      <p className="text-gray-600 mb-6">Get local flood warnings and water damage prevention tips for your area.</p>

      {submitted ? (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">
                Thank you for subscribing! You&apos;ll receive flood alerts for your area.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                ZIP Code
              </label>
              <input
                type="text"
                id="zipCode"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Your ZIP code"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alert Preferences
              </label>
              <div className="flex space-x-4">
                <div className="flex items-center">
                  <input
                    id="email-alerts"
                    name="alert-type"
                    type="radio"
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    value="email"
                    checked={alertType === 'email'}
                    onChange={() => setAlertType('email')}
                  />
                  <label htmlFor="email-alerts" className="ml-2 text-sm text-gray-700">
                    Email Only
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="sms-alerts"
                    name="alert-type"
                    type="radio"
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    value="sms"
                    checked={alertType === 'sms'}
                    onChange={() => setAlertType('sms')}
                  />
                  <label htmlFor="sms-alerts" className="ml-2 text-sm text-gray-700">
                    SMS (Coming Soon)
                  </label>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="w-full bg-[#004C8C] text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition flex justify-center items-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Subscribing...
                  </>
                ) : (
                  'Subscribe to Alerts'
                )}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

export default function FloodAlertForm({ className = '' }) {
  if (!config.features.enableFloodAlerts) {
    return null;
  }
  return <FloodAlertFormInner className={className} />;
}